import {
  ApiHttpError,
  mapUserProfileDto,
  mapWalletDto,
  type UserProfileDto,
  type WalletDto,
} from '@/types';

import type {
  AuthProvider,
  GoogleAuthConfig,
  LoginResult,
} from '../authProvider';
import {
  httpRequest,
  httpRequestWithRefresh,
  type HttpClientConfig,
} from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * 後端 ``/api/front_auth/google_login`` 成功時 ``result`` 的 schema。
 * 對齊 spec：``creative_uid`` 是頂層識別碼、``profile`` 是使用者資料、``wallet``
 * 是錢包、``is_new_user`` 是首次登入旗標。
 */
interface GoogleLoginResultDto {
  creative_uid: string;
  profile: UserProfileDto;
  wallet: WalletDto;
  is_new_user: boolean;
}

/**
 * 後端 ``/api/front_auth/google/config`` 的 ``result`` schema。
 * Backend 從 permission-service ``/root/google_oauth/client`` 拿來、轉一次**只**
 * 回 ``client_id``（``client_secret`` / ``developer_token`` 永遠留 backend）。
 */
interface GoogleConfigResultDto {
  client_id: string;
}

/**
 * 真正會打後端的 :type:`AuthProvider` 實作。
 *
 * 對應 4 個端點(R45 起 `/refresh` 410 Gone 從 client 退出):
 * - ``GET  /api/front_auth/google/config``  拿 OAuth client_id(GIS 初始化用)
 * - ``POST /api/front_auth/google_login``    Google id_token 登入(CSRF exempt)
 * - ``POST /api/front_auth/logout``          登出,**需 X-CSRF-Token**(httpRequest 自動帶)
 * - ``GET  /api/front_auth/login_status``    確認目前 session(cookie 自動送)
 *
 * @remarks
 * 認證一律走 HttpOnly cookie(``credentials: 'include'`` 在 :func:`httpRequest`
 * 已包好)。前端不再手動加 ``Authorization`` header,也不再做 refresh chain
 * (Round 50:audit 確認 R45 spec §1.4 已砍 refresh chain — 401 直接 onAuthLost)。
 */
export function createHttpAuthProvider(config: HttpClientConfig): AuthProvider {
  return {
    async getGoogleConfig(): Promise<GoogleAuthConfig> {
      const envelope = await httpRequest<GoogleConfigResultDto>(
        config,
        'GET',
        '/api/front_auth/google/config',
      );
      const result = envelope.result;
      if (!result?.client_id) {
        // 503:permission-service 不可達 / 沒回 client_id(對齊 AuthProvider 介面 JSDoc)。
        // 不走 unwrapResult —— 此處要連「result 在但 client_id 空字串」也擋,語意比單純
        // null/undefined 判定更窄;且缺值代表上游服務不可用(503)而非契約破裂(500)。
        throw new ApiHttpError(503, {
          code: 'GOOGLE_CONFIG_UNAVAILABLE',
          message: 'Backend response missing client_id',
        });
      }
      return { clientId: result.client_id };
    },

    async signIn(idToken: string): Promise<LoginResult> {
      const envelope = await httpRequest<GoogleLoginResultDto>(
        config,
        'POST',
        '/api/front_auth/google_login',
        { id_token: idToken },
      );
      // R42.5(2026-05-17):BE MR !26 收尾砍 `envelope.access_token` 欄位
      // (cookie-only 認證 + Pydantic extra="forbid")。原本 `!accessToken` guard 已砍 —
      // 只要 BE 回 result 完整就算成功(cookie 在 same-origin 自動帶)。R81:缺 result
      // 改走共用 unwrapResult → 500 MISSING_RESULT(原 200 孤例會讓 caller status 分支誤判)。
      const result = unwrapResult(envelope, 'auth.signIn');
      return {
        creativeUid: result.creative_uid,
        user: mapUserProfileDto(result.profile),
        wallet: mapWalletDto(result.wallet),
        isNewUser: result.is_new_user,
      };
    },

    async signOut(): Promise<void> {
      try {
        await httpRequest<boolean>(config, 'POST', '/api/front_auth/logout');
      } catch (e) {
        /* logout 失敗不影響前端清狀態：cookie 已被後端清、或我們把 in-memory state
           丟掉，session 都算結束。網路錯誤時前端依然視為已登出。 */
        if (!(e instanceof ApiHttpError)) throw e;
      }
    },

    async isAuthenticated(): Promise<boolean> {
      try {
        // Round 50 (R45+) — 無 refresh chain;`httpRequestWithRefresh` 只在 401 +
        // AUTH_INVALID_TOKEN 觸發 onAuthLost(清 store + RequireAuth navigate /login)。
        // 401 直接 false。
        await httpRequestWithRefresh<boolean>(
          config,
          'GET',
          '/api/front_auth/login_status',
        );
        return true;
      } catch (e) {
        if (e instanceof ApiHttpError) return false;
        throw e;
      }
    },
  };
}
